import { useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import {
  ArrowLeft,
  Shield,
  FileText,
  Download,
  Box,
  CircleDot,
  FileBox,
  BookOpen,
  AlertCircle
} from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { API_BASE_URL } from "../../config/config";
import { lobMap } from "../../config/constant";
import { RecommendationBlock } from "./recommendation-block";
import { analyzeProperty } from "../../api/analysisProperty";
import { useSelector } from "react-redux";
import { fetchApplicationDataByIdAndName } from "../../api/userService";

export default function LobDetailsPage() {
  const { lobName, appId } = useParams();
  const [lobData, setLobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [propertyAnalysis, setPropertyAnalysis] = useState(null);

  const selectedApplicationData = useSelector(
    (state) => state.intakeData.selectedApplicationData || {}
  );

  const hasFetched = useRef(false);

  useEffect(() => {
    if (
      !selectedApplicationData?.application_id ||
      !selectedApplicationData?.customer_name ||
      hasFetched.current
    ) {
      return;
    }

    hasFetched.current = true;

    const fetchApplicationData = async () => {
      setLoading(true);
      try {
        await fetchApplicationDataByIdAndName(
          selectedApplicationData?.customer_name,
          selectedApplicationData?.application_id
        );

        // ✅ DO NOT await this; run in background
        analyzeProperty({
          applicationId: appId,
          address: selectedApplicationData?.agent_address
        })
          .then((result) => {
            setPropertyAnalysis(result);
          })
          .catch((err) => {
            console.error("Property analysis failed:", err);
          });
      } catch (err) {
        console.error("Error fetching application data:", err);
        setError(err.message || "Failed to fetch application data");
      } finally {
        setLoading(false);
      }
    };

    if (lobName === "property") fetchApplicationData();
  }, [selectedApplicationData]);

  useEffect(() => {
    const fetchLobData = async () => {
      setLoading(true);
      setError(null);
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      if (!accessToken || !refreshToken) {
        setError("Authentication required.");
        setLoading(false);
        return;
      }
      try {
        const apiUrl = `${API_BASE_URL}/insureai/ml/intake/applications/v2/get_dif_info_by_lob?application_id=${appId}&lob=${lobName}`;

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "X-Access-Token": accessToken,
            "X-Refresh-Token": refreshToken
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setLobData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (appId && lobName) {
      fetchLobData();
    }
  }, [appId, lobName]);

  if (loading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 text-red-600">Error: {error}</div>
    );
  }

  const applicationSummary = lobData?.summary || "Data not available";
  const aiInsights = lobData?.insights?.html || "Data not available";
  const aiInsightsLinks =
    lobData?.insights?.named_links || "Data not available";

  const guidance = lobData?.guidance?.html || "Data not available";
  const guidanceLinks = lobData?.guidance?.named_links || "Data not available";
  const riskScore = lobData?.score || "N/A";
  const infos = lobData?.infos || [];

  // Determine if Desk Manual tab should be visible
  return (
    <div className="mx-auto p-6 bg-gray-100 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.close()} // Changed to window.close()
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-gray-700" />
            <h1 className="text-xl font-semibold text-gray-800">
              {lobMap[lobName]}
            </h1>
            <span className="text-gray-500 text-sm">
              Application ID: {appId}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative inline-flex items-center justify-center">
            <div
              className={`w-12 h-12 rounded-full border-4 ${
                parseFloat(riskScore) > 7
                  ? "border-orange-500"
                  : parseFloat(riskScore) > 5
                  ? "border-orange-500"
                  : "border-red-500"
              } flex items-center justify-center`}
            >
              <span
                className={`text-lg font-bold ${
                  parseFloat(riskScore) > 7
                    ? "text-green-500"
                    : parseFloat(riskScore) > 5
                    ? "text-orange-500"
                    : "text-red-500"
                }`}
              >
                {riskScore}
              </span>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-600">Risk Score</p>
              <p className="text-xs text-gray-500">out of 10</p>
            </div>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2">
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="grid grid-cols-12 gap-6">
        {/* <div className="col-span-3 space-y-6">
          <Card className="p-4 shadow-sm bg-white">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              <h3 className="text-base font-semibold text-gray-800">
                Key Metrics
              </h3>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              {displayedKeyMetrics.map((metric, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {metric.icon}
                    <span>{metric.label}:</span>
                  </div>
                  <span className="font-medium">{metric.value}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Quick Actions Card */}
        {/* <Card className='p-4 shadow-sm'>
            <h3 className='text-base font-semibold text-gray-800 mb-4'>
              Quick Actions
            </h3>
            <div className='space-y-3'>
              {displayedQuickActions.map((action, index) => (
                <Button
                  key={index}
                  variant='outline'
                  className='w-full justify-start text-gray-700 hover:bg-gray-50'
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </div>
          </Card>
        </div> */}

        {/* Right Column: Overview and Tabs */}
        <div className="col-span-12">
          {/* Overview Section (moved out of tabs) */}
          <Card className="p-4 shadow-sm mb-6 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-orange-500" />
              <h3 className="text-base font-semibold text-gray-800">
                Application Summary
              </h3>
            </div>
            <p className="text-sm text-gray-700">{applicationSummary}</p>
          </Card>

          <div className="col-span-12 mt-6">
            {/* Tabs Section */}
            <Tabs defaultValue="ai-insights">
              <TabsList
                className={`grid w-full bg-white rounded-lg shadow-sm ${
                  lobName === "property" &&
                  propertyAnalysis?.status === "SUCCESS"
                    ? "grid-cols-3"
                    : "grid-cols-2"
                }`}
              >
                <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
                <TabsTrigger value="guidance">Guidance</TabsTrigger>
                {lobName === "property" &&
                  propertyAnalysis?.status === "SUCCESS" && (
                    <TabsTrigger value="risk-summary">
                      Property Analysis
                    </TabsTrigger>
                  )}
              </TabsList>
              <TabsContent value="ai-insights" className="mt-4">
                <Card className="p-4 shadow-sm bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-orange-500" />
                    <h3 className="text-base font-semibold text-gray-800">
                      AI Insights
                    </h3>
                  </div>
                  <div className="h-[250px] overflow-y-auto pr-4">
                    <div
                      className="prose text-sm text-gray-700 [&_a]:text-blue-600 [&_a]:hover:underline [&_a]:cursor-pointer"
                      dangerouslySetInnerHTML={{
                        __html: aiInsights.replace(
                          /<a /g,
                          '<a target="_blank" rel="noopener noreferrer" '
                        )
                      }}
                    />
                  </div>
                  {aiInsightsLinks && aiInsightsLinks.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">
                        Related Links:
                      </h4>
                      <ul className="list-disc pl-5 space-y-1">
                        {aiInsightsLinks.map((link, index) => (
                          <li key={index}>
                            <a
                              href={link[1]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {link[0]}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </TabsContent>
              <TabsContent value="guidance" className="mt-4">
                <Card className="p-4 shadow-sm bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-orange-500" />
                    <h3 className="text-base font-semibold text-gray-800">
                      Guidance
                    </h3>
                  </div>
                  <div className="h-[250px] overflow-y-auto pr-4">
                    <div
                      className="prose text-sm text-gray-700 [&_a]:text-blue-600 [&_a]:hover:underline [&_a]:cursor-pointer"
                      dangerouslySetInnerHTML={{
                        __html: guidance.replace(
                          /<a /g,
                          '<a target="_blank" rel="noopener noreferrer" '
                        )
                      }}
                    />
                  </div>
                  {guidanceLinks && guidanceLinks.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">
                        Related Links:
                      </h4>
                      <ul className="list-disc pl-5 space-y-1">
                        {guidanceLinks.map((link, index) => (
                          <li key={index}>
                            <a
                              href={link[1]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {link[0]}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              </TabsContent>
              {lobName === "property" &&
                propertyAnalysis?.status === "SUCCESS" && (
                  <TabsContent value="risk-summary">
                    {!propertyAnalysis ? (
                      <div className="text-sm text-gray-500 p-4">
                        Loading property analysis...
                      </div>
                    ) : (
                      propertyAnalysis?.analysis &&
                      Object.entries(propertyAnalysis?.analysis)

                        .filter(([key, value]) => {
                          // Exclude metadata and ensure value exists
                          if (key === "assessment_metadata" || !value)
                            return false;

                          if (
                            ["string", "number", "boolean"].includes(
                              typeof value
                            )
                          ) {
                            return (
                              <RecommendationBlock
                                key={key}
                                heading={key}
                                sections={[String(value)]}
                              />
                            );
                          }

                          // Exclude empty arrays
                          if (Array.isArray(value)) {
                            return value.some(
                              (item) =>
                                item &&
                                (item.section_summary?.length ||
                                  item.items?.length ||
                                  typeof item === "string")
                            );
                          }

                          if (
                            key === "detailed_risk_analysis" &&
                            Array.isArray(value)
                          ) {
                            return value.map((area, i) => (
                              <RecommendationBlock
                                key={`${key}-${i}`}
                                heading={area.risk_area_name}
                                sections={[
                                  {
                                    title: "Summary",
                                    color: "yellow",
                                    items:
                                      area.identified_hazards_or_observations ||
                                      []
                                  }
                                ]}
                              />
                            ));
                          }

                          // Exclude empty objects
                          if (typeof value === "object") {
                            return Object.values(value).some((entry) => {
                              if (!entry) return false;
                              if (Array.isArray(entry)) return entry.length > 0;
                              return (
                                typeof entry === "string" ||
                                typeof entry === "number" ||
                                typeof entry === "object"
                              );
                            });
                          }

                          return false;
                        })
                        .map(([key, value], index) => {
                          const formattedKey = key
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase());

                          // ✅ CASE 1: value is an array
                          if (Array.isArray(value)) {
                            return value
                              .filter(
                                (item) =>
                                  item &&
                                  (item.section_summary?.length ||
                                    item.items?.length ||
                                    item.description ||
                                    typeof item === "string")
                              )
                              .map((item, subIndex) => {
                                const sectionTitle =
                                  item.section_title ||
                                  item.title ||
                                  `${formattedKey} ${subIndex + 1}`;

                                const summary =
                                  item.section_summary || item.items || [];

                                if (!summary || summary.length === 0)
                                  return null;

                                return (
                                  <RecommendationBlock
                                    key={`${key}-${subIndex}`}
                                    heading={sectionTitle}
                                    sections={[
                                      {
                                        title: "Summary",
                                        color: "yellow",
                                        items: summary
                                      }
                                    ]}
                                  />
                                );
                              });
                          }

                          // ✅ CASE 2: value is an object
                          if (typeof value === "object") {
                            const validSections = Object.entries(value)
                              .filter(([_, items]) => {
                                if (!items) return false;
                                if (Array.isArray(items))
                                  return items.length > 0;
                                return true;
                              })
                              .map(([innerKey, items]) => ({
                                title: innerKey
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c) => c.toUpperCase()),
                                color: "yellow",
                                items: Array.isArray(items)
                                  ? items
                                  : typeof items === "object"
                                  ? [items]
                                  : typeof items === "string"
                                  ? [{ description: items }]
                                  : typeof items === "number"
                                  ? [{ description: items }]
                                  : typeof items === "boolean"
                                  ? [{ description: items }]
                                  : []
                              }));

                            if (validSections.length === 0) return null;

                            return (
                              <RecommendationBlock
                                key={key}
                                heading={formattedKey}
                                sections={validSections}
                              />
                            );
                          }

                          return null;
                        })
                    )}
                  </TabsContent>
                )}
              {/* <TabsContent value='guidance' className='mt-4'>
                <Card className='p-4 shadow-sm bg-white'>
                  <div className='flex items-center gap-2 mb-4'>
                    <FileText className='h-5 w-5 text-orange-500' />
                    <h3 className='text-base font-semibold text-gray-800'>
                      Guidance
                    </h3>
                  </div>
                  <div
                    className='prose text-sm text-gray-700'
                    dangerouslySetInnerHTML={{ __html: guidance }}
                  />
                </Card>
              </TabsContent> */}
            </Tabs>
          </div>
          {/* Extraction Details Section */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-6">
              <FileBox className="h-6 w-6 text-orange-500" />
              <h2 className="text-lg  font-semibold text-gray-800">
                Extraction Details
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {infos.length > 0 ? (
                infos.map((info, index) => (
                  <Card
                    key={index}
                    className="p-6 bg-white hover:shadow-md transition-shadow duration-200 h-[400px] flex flex-col"
                  >
                    <div className="flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-4">
                        <BookOpen className="h-5 w-5 text-orange-500" />
                        <h3 className="text-base font-semibold text-gray-800">
                          {info.name || `Section ${index + 1}`}
                        </h3>
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <div className="h-full overflow-y-auto pr-4">
                          <div
                            className="prose text-sm text-gray-700 [&_a]:text-blue-600 [&_a]:hover:underline [&_a]:cursor-pointer max-w-full"
                            dangerouslySetInnerHTML={{
                              __html: info.html.replace(
                                /<a /g,
                                '<a target="_blank" rel="noopener noreferrer" '
                              )
                            }}
                          />
                          {info.named_links && info.named_links.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-orange-500" />
                                Related Resources
                              </h4>
                              <ul className="space-y-2">
                                {info.named_links.map((link, linkIndex) => (
                                  <li key={linkIndex}>
                                    <a
                                      href={link[1]}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline flex items-center gap-2 text-sm break-all"
                                    >
                                      <CircleDot className="h-3 w-3 flex-shrink-0" />
                                      {link[0]}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <div className="col-span-2">
                  <Card className="p-6 bg-white text-center">
                    <Box className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">
                      No extraction details available.
                    </p>
                  </Card>
                </div>
              )}
            </div>
          </div>
          {/* Images Analysis Section */}
          {lobName === "property" && propertyAnalysis?.status === "SUCCESS" && (
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-6">
                <FileBox className="h-6 w-6 text-orange-500" />
                <h2 className="text-lg  font-semibold text-gray-800">
                  Images Analysis
                </h2>
              </div>
              <Card className="p-4 shadow-sm mb-6 bg-white">
                <div className="flex border rounded-md overflow-hidden w-fit mb-4">
                  <button className="px-4 py-2 text-sm font-medium text-gray-500 bg-gray-100 border-r">
                    Photos
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-white bg-black">
                    Satellite Images
                  </button>
                </div>

                {propertyAnalysis ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                    {Array.isArray(
                      propertyAnalysis?.analysis?.assessment_metadata
                        ?.source_image_references
                    ) &&
                      propertyAnalysis?.analysis?.assessment_metadata?.[
                        "source_image_references"
                      ]?.map((img, idx) => (
                        <>
                          {img?.signed_url && (
                            <div
                              key={idx}
                              className="rounded-md overflow-hidden border border-gray-200 shadow-sm bg-gray-50"
                            >
                              <img
                                src={img.signed_url}
                                alt={img.filename}
                                className="h-32 w-full object-cover"
                              />
                              <div className="bg-gray-800 text-white text-xs font-medium text-center py-1 px-2">
                                {img.filename
                                  .replace(/_/g, " ")
                                  .replace(/\.jpg$/i, "")}
                              </div>
                            </div>
                          )}
                        </>
                      ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Loading satellite images...
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
